import { setDeploymentTrack } from "@/core/deployTrackStore";
import { GLOBAL_CONFIG } from "@/global-config";
import { t } from "@/locales/i18n";
import { getItem, removeItem, setItem } from "@/utils/storage";
import axios, {
	type AxiosRequestConfig,
	type AxiosError,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";
import type { Result } from "#/api";
import { StorageEnum } from "#/enum";

const SPECIAL_CODE = 50001;
const LOGIN_URL = "/auth/oauth2/token";

export type APIRequestConfig = AxiosRequestConfig & {
	silent?: boolean;
	returnResult?: boolean;
};

// 生成登录/刷新 token 的 Basic Auth header
const { oatuh2PasswordSecret, oauth2PasswordClient } = GLOBAL_CONFIG;
const credentials = `${oauth2PasswordClient}:${oatuh2PasswordSecret}`;
const base64Credentials = btoa(credentials);
const authHeaders = {
	Authorization: `Basic ${base64Credentials}`,
	"Content-Type": "application/x-www-form-urlencoded",
	"TENANT-ID": 1,
};

// 刷新 token 状态管理
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshRetryCount = 0;
const MAX_REFRESH_RETRY = 5;

const subscribeTokenRefresh = (callback: (token: string) => void) => {
	refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken: string) => {
	refreshSubscribers.forEach((callback) => callback(newToken));
	refreshSubscribers = [];
};

const logout = () => {
	console.log("logout");
	removeItem(StorageEnum.UserToken);
	removeItem(StorageEnum.TokenExpire);
	removeItem(StorageEnum.RefreshToken);
	removeItem(StorageEnum.RefreshTokenExpire);
	location.href = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/login`;
};

/**
 * 检查 refresh token 是否有效
 */
const isRefreshTokenValid = (): boolean => {
	const refreshToken = getItem<string>(StorageEnum.RefreshToken);
	const refreshTokenExpire = getItem<number>(StorageEnum.RefreshTokenExpire);

	if (!refreshToken || !refreshTokenExpire) {
		return false;
	}

	// 检查是否过期（预留 60 秒缓冲）
	const now = Math.floor(Date.now() / 1000);
	return refreshTokenExpire > now + 60;
};

/**
 * 使用 refresh token 刷新 access token
 */
const refreshAccessToken = async (): Promise<string> => {
	const refreshToken = getItem<string>(StorageEnum.RefreshToken);

	if (!refreshToken) {
		throw new Error("No refresh token available");
	}

	const response = await axios.post(
		`${GLOBAL_CONFIG.apiBaseUrl}${LOGIN_URL}`,
		new URLSearchParams({
			scope: "server",
			refresh_token: refreshToken,
			grant_type: "refresh_token",
		}),
		{
			headers: authHeaders,
		},
	);

	const { data } = response.data || {};
	if (!data?.access_token) {
		throw new Error("Failed to refresh token");
	}

	// 保存新的 token
	setItem(StorageEnum.UserToken, data.access_token);
	setItem(StorageEnum.TokenExpire, data.exp);
	if (data.refresh_token) {
		setItem(StorageEnum.RefreshToken, data.refresh_token);
		setItem(StorageEnum.RefreshTokenExpire, data.refresh_token_exp);
	}

	return data.access_token;
};

/**
 * 处理 token 过期，尝试刷新
 */
const handleTokenExpired = async (
	originalRequest: InternalAxiosRequestConfig,
): Promise<AxiosResponse> => {
	// 检查重试次数
	if (refreshRetryCount >= MAX_REFRESH_RETRY) {
		console.warn(`Token refresh exceeded max retry count (${MAX_REFRESH_RETRY}), logging out...`);
		refreshRetryCount = 0;
		logout();
		return Promise.reject(new Error("Token refresh exceeded max retry count"));
	}

	// 检查 refresh token 是否有效
	if (!isRefreshTokenValid()) {
		refreshRetryCount = 0;
		logout();
		return Promise.reject(new Error("Refresh token expired"));
	}

	// 如果正在刷新，等待刷新完成
	if (isRefreshing) {
		return new Promise((resolve) => {
			subscribeTokenRefresh((newToken: string) => {
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				resolve(axiosInstance(originalRequest));
			});
		});
	}

	isRefreshing = true;
	refreshRetryCount++;

	try {
		const newToken = await refreshAccessToken();
		// 刷新成功，重置计数器
		refreshRetryCount = 0;
		onTokenRefreshed(newToken);
		originalRequest.headers.Authorization = `Bearer ${newToken}`;
		return axiosInstance(originalRequest);
	} catch (error) {
		logout();
		return Promise.reject(error);
	} finally {
		isRefreshing = false;
	}
};
const axiosInstance = axios.create({
	baseURL: GLOBAL_CONFIG.apiBaseUrl,
	headers: { "Content-Type": "application/json;charset=utf-8" },
	paramsSerializer: {
			serialize: (params) => {
				const searchParams = new URLSearchParams();
				for (const [key, value] of Object.entries(params)) {
					if (Array.isArray(value)) {
						for (const item of value) {
							if (item !== null && item !== undefined && item !== "") {
								searchParams.append(key, String(item));
							}
						}
						continue;
					}
					if (value !== null && value !== undefined && value !== "") {
						searchParams.append(key, String(value));
					}
				}
				return searchParams.toString();
			},
		},
});

axiosInstance.interceptors.request.use(
	(config) => {
		// TODO: 处理分页参数
		if (config?.params?.pageSize) {
			config.params.size = config.params.pageSize;
			// biome-ignore lint/performance/noDelete: delete the original pageSize to avoid confusion
			delete config.params.pageSize;
		}
		// 在请求头中添加 X-Request-Context-Custom
		const customHeader = JSON.stringify({
			appVersion: "1.0.0",
			apiVersion: "v1",
		});

		config.headers["X-Request-Context-Custom"] = customHeader;

		// 特殊处理登录接口
		if (config.url === "/auth/oauth2/token") {
			return config;
		}

		// 添加 Authorization 头
		const token = getItem(StorageEnum.UserToken);
		if (token && typeof token === "string") {
			config.headers.Authorization = `Bearer ${token}`;
		}

		return config;
	},
	(error) => Promise.reject(error),
);

const handleError = (errMsg: string) => {
	toast.error(errMsg);
};

/**
 * 从响应头读取网关裁决出的部署轨道。
 * 网关只在命中 PRE 规则时回写该头，缺头即视为稳定轨道。
 */
const captureDeploymentTrack = (res: AxiosResponse<unknown>) => {
	const raw = res.headers?.["x-deployment-track"];
	setDeploymentTrack(raw === "PRE" ? "PRE" : "A");
};
axiosInstance.interceptors.response.use(
	(res: AxiosResponse<Result<any>>) => {
		// 记录网关裁决出的部署轨道（同源请求，无需 CORS 暴露该头）
		captureDeploymentTrack(res);

		// 处理 blob 响应（文件下载）
		if (res.config.responseType === "blob" || res.data instanceof Blob) {
			return res.data;
		}

		enum ResultStatus {
			EXPIRED = 0, // 登录态过期
			LOGIN_FAILED = 1, // 登录时失败
			ERROR = 2, // 其他错误
			SUCCESS = 3, // 成功
			MUTATE_SUCCESS = 4, // 写操作成功
			SPECIAL_RESPONSE = 5, // 特殊响应，如 code=50001
		}

		const { code, ok, data } = res.data || {};
		let resultStatus = ResultStatus.ERROR;
		if (code === 1001) {
			resultStatus = ResultStatus.EXPIRED;
		}
		// TODO: 登录是否成功不应该由前端去判断
		const isLoginFailed =
			res.config.url === LOGIN_URL &&
			(!data || !data.access_token || !data.refresh_token);
		if (isLoginFailed) {
			resultStatus = ResultStatus.LOGIN_FAILED;
		}

		// 写操作统一处理
		const isMutateSuccess = ok && code === 0 && res.config.method !== "GET";
		if (isMutateSuccess) {
			resultStatus = ResultStatus.MUTATE_SUCCESS;
		}

		if (ok && code === 0) {
			resultStatus = ResultStatus.SUCCESS;
		}
		if (code === SPECIAL_CODE) {
			resultStatus = ResultStatus.SPECIAL_RESPONSE;
		}

		// 检查 data 是否为 false，如果是则当作错误处理
		if ((resultStatus === ResultStatus.SUCCESS || resultStatus === ResultStatus.MUTATE_SUCCESS) && data === false) {
			handleError(res.data.msg || t("sys.api.errorTip"));
			return Promise.reject(res);
		}

		const requestConfig = res.config as APIRequestConfig;
		const silent = requestConfig.silent === true;
		const returnResult = requestConfig.returnResult === true;
		switch (resultStatus) {
			case ResultStatus.EXPIRED:
				// 尝试刷新 token
				return handleTokenExpired(res.config);
			case ResultStatus.LOGIN_FAILED:
				if (!silent) handleError(res.data.msg || t("sys.api.errorTip"));
				return Promise.reject(res);
			case ResultStatus.ERROR:
				if (!silent) handleError(res.data.msg || t("sys.api.errorTip"));
				return Promise.reject(res);
			case ResultStatus.SUCCESS:
				return returnResult ? res.data : data;
			case ResultStatus.MUTATE_SUCCESS:
				if (!silent) toast.success(t("sys.api.operationSuccess"));
				return returnResult ? res.data : data;
			case ResultStatus.SPECIAL_RESPONSE:
				return res.data;
			default:
				if (!silent) handleError(res.data.msg || t("sys.api.errorTip"));
				return Promise.reject(res);
		}
	},
	(error: AxiosError<Result>) => {
		const { response, message, config } = error || {};
		const isAuthRequest = config?.url?.includes(LOGIN_URL);

		// 401 且不是登录接口，尝试刷新 token
		if (response?.status === 401 && !isAuthRequest && config) {
			return handleTokenExpired(config as InternalAxiosRequestConfig);
		}

		const errMsg = response?.data?.msg || message || t("sys.api.errorTip");
		handleError(errMsg);
		return Promise.reject(error);
	},
);

class APIClient {
	get<T = unknown>(config: APIRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "GET" });
	}
	post<T = unknown>(config: APIRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "POST" });
	}
	put<T = unknown>(config: APIRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PUT" });
	}
	delete<T = unknown>(config: APIRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "DELETE" });
	}
	request<T = unknown>(config: APIRequestConfig): Promise<T> {
		return axiosInstance.request<any, T>(config);
	}
}

export default new APIClient();

export const uploadAxiosInstance = axios.create({
	baseURL: GLOBAL_CONFIG.apiBaseUrl,
	headers: { "Content-Type": "multipart/form-data" },
});

uploadAxiosInstance.interceptors.request.use(
	(config) => {
		// 添加 Authorization 头
		const token = getItem(StorageEnum.UserToken);
		if (token && typeof token === "string") {
			config.headers.Authorization = `Bearer ${token}`;
		}

		return config;
	},
	(error) => Promise.reject(error),
);
